/*       */
import memoize from 'memoize-one';
import moment from 'moment';
import joypixels from 'emoji-toolkit';

import { isEmpty } from 'app/utils/utils';
import { rocketHost } from 'app/utils/env';
import { get } from 'app/utils/lo/lo';
import { getSystemMessageByType } from 'app/utils/chat/chatMessageTypes';

// RC-supported emoji dataset is fetched lazily from `public/data/rcSupportedEmojis.json`
// (served at `/data/rcSupportedEmojis.json` — NOT under `/chat/` because ingress routes
// that prefix to Rocket.Chat in deployed environments). Keeps the ~135 KB file out
// of the main bundle. Modules that need the data call `getRcEmojiData()` after
// `ensureRcEmojiData()` has resolved. Unloaded consumers fall back gracefully
// (joypixels → raw shortcode).
let rcEmojiDataCache = null;
let rcEmojiDataPromise = null;

export const getRcEmojiData = () => rcEmojiDataCache;

export const ensureRcEmojiData = () => {
    if (rcEmojiDataCache) return Promise.resolve(rcEmojiDataCache);
    if (rcEmojiDataPromise) return rcEmojiDataPromise;
    rcEmojiDataPromise = fetch('/data/rcSupportedEmojis.json')
        .then(res => {
            if (!res.ok) throw new Error(`rcSupportedEmojis.json fetch failed: ${res.status}`);
            return res.json();
        })
        .then(data => {
            rcEmojiDataCache = data;
            return data;
        })
        .catch(err => {
            rcEmojiDataPromise = null;
            throw err;
        });
    return rcEmojiDataPromise;
};

export const setRcEmojiDataForTest = (data) => {
    rcEmojiDataCache = data;
};

export const KEYS = {
    ESCAPE: 'Escape',
    UP: 'ArrowUp',
    DOWN: 'ArrowDown',
    LEFT: 'ArrowLeft',
    RIGHT: 'ArrowRight',
    ENTER: 'Enter',
};

export const validTypes = new Set(['entity', 'process', 'task']);

export const oneMinute = 60000;
export const refreshRate = 10 * oneMinute;

const isDateValid = date => {
    return date instanceof Date && !isNaN(date);
};

export const getSubscriptionByRel = memoize((subscriptions, rel) => {
    if (!rel || !rel.id || !rel.type) {
        return null;
    }
    const { id } = rel;
    const subscription = (subscriptions || []).find(subscription => subscription.rel.id === id);

    return subscription ? subscription : null;
});

export const getSubscriptionByRid = memoize((subscriptions, rid) => {
    if (isEmpty(subscriptions) || isEmpty(rid)) {
        return null;
    }
    const subscription = (subscriptions || []).find(subscription => subscription.rid === rid);

    return subscription ? subscription : null;
});

export const getSubscriptionByUsername = memoize((subscriptions, username) => {
    if (isEmpty(subscriptions) || isEmpty(username)) {
        return null;
    }
    const subscription = (subscriptions || []).find(subscription => subscription.name === username);

    return subscription ? subscription : null;
});

// search chats by name || id
export const searchChats = (keyword, chats) => {
    const query = !isEmpty(keyword) ? keyword.trim().toLowerCase() : '';
    let result = [];

    function searchByNameOrId(chat) {
        const { rel, customFields } = chat || {};
        const isEntityChat = !isEmpty(customFields);
        let isFound = false;
        if (isEmpty(rel)) return isFound;
        const { id, name } = rel;

        if (!isEntityChat) {
            if (!name) return isFound;
            const qName = name.toLowerCase();
            isFound = qName.indexOf(query) >= 0;
            return isFound;
        } else {
            if (name) {
                const relName = name.toLowerCase();
                isFound = relName.indexOf(query) >= 0;
                if (isFound) return true;
            }
            if (id) {
                const relId = id.toLowerCase();
                isFound = relId.indexOf(query) >= 0;
                if (isFound) return true;
            }
            return isFound;
        }
    }
    result = (chats || []).filter(searchByNameOrId);

    return result;
};

export const sortChats = (sortBy, chats) => {
    const parsedData = [...chats];
    switch (sortBy) {
        case 'name':
            parsedData.sort((data1, data2) => {
                const relName1 = data1.rel.name.toUpperCase();
                const relName2 = data2.rel.name.toUpperCase();
                return relName1 < relName2 ? -1 : relName1 > relName2 ? 1 : 0;
            });
            break;
        case 'activity':
            parsedData.sort((data1, data2) => {
                const { lastMessage: lastMessage1 } = data1;
                const { lastMessage: lastMessage2 } = data2;
                const date1 = !isEmpty(lastMessage1) ? new Date(lastMessage1.createDate) : new Date(data1.updatedAt);
                const date2 = !isEmpty(lastMessage2) ? new Date(lastMessage2.createDate) : new Date(data2.updatedAt);
                return date2 - date1;
            });
            break;
        default:
            return chats;
    }
    return parsedData;
};

export const getChatRequestType = type => {
    let chatType = '';
    const isEntityChat = !['channel', 'direct', 'group', 'live'].includes(type);
    if (isEntityChat || type === 'group') {
        chatType = 'groups';
    } else {
        switch (type) {
            case 'direct':
                chatType = 'im';
                break;
            case 'channel':
                chatType = 'channels';
                break;
            default:
                chatType = type;
                break;
        }
    }
    return chatType;
};

export const replaceAll = (str, find, replace) => {
    const escapedFind = find.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1'); // eslint-disable-line
    return str.replace(new RegExp(escapedFind, 'g'), replace);
};

export const validateEmail = email => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
};

export const checkIfUserIsMember = (userId, data) => {
    if (!data) return false;
    const user = (data?.users || []).find(d => d.user?.id === userId);
    return Boolean(user);
};

export const getRoomImages = messages => {
    const imageTypes = ['image/svg+xml', 'image/gif', 'image/jpeg', 'image/jpg', 'image/png'];

    return !isEmpty(messages)
        ? (messages || [])
            .filter(({ file }) => !isEmpty(file) && imageTypes.includes(file?.type))
            .map(({ file }) => {
                const { id: fileId, name: fileName } = file || {};
                const downloadUrl = `${document.location.protocol}//${rocketHost}/file-upload/${fileId}/${fileName}?download`;
                const src = `/chat/file-upload/${fileId}/${fileName}`;
                return { id: file.id, src, downloadUrl };
            })
        : [];
};

export const lastEditableMessage = (messages, currentUsername) => {
    for (let i = messages.length - 1; i >= 0; i--) {
        if (isMessageEditable(messages[i], currentUsername)) {
            return i;
        }
    }
    return -1;
};

export const getNextEditableMessage = (messages, messageIndex, direction, currentUsername) => {
    if (direction === 'up') {
        for (let i = messageIndex - 1; i >= 0; i--) {
            if (isMessageEditable(messages[i], currentUsername)) {
                return i;
            }
        }
    } else if (direction === 'down') {
        for (let i = messageIndex + 1; i < messages.length; i++) {
            if (isMessageEditable(messages[i], currentUsername)) {
                return i;
            }
        }
    }
    return -1;
};

export const isMessageEditable = (message, currentUsername) => {
    const { user, type, file } = message || {};
    if (!type && isEmpty(file) && user.username === currentUsername) {
        return true;
    }
    return false;
};

export const getSelectedMessage = (messages, editMsgId, currentUsername, direction) => {
    const currentEditedMesssageIndex = messages.findIndex(msg => msg.id === editMsgId);
    let nextMessageIndex = 0;

    if (direction === 'up') {
        nextMessageIndex =
            currentEditedMesssageIndex !== 0
                ? getNextEditableMessage(messages, currentEditedMesssageIndex, 'up', currentUsername)
                : lastEditableMessage(messages, currentUsername);
        nextMessageIndex = nextMessageIndex !== -1 ? nextMessageIndex : lastEditableMessage(messages, currentUsername);
    } else if (direction === 'down') {
        if (currentEditedMesssageIndex !== -1) {
            nextMessageIndex =
                currentEditedMesssageIndex === messages.length - 1 ? -1 : getNextEditableMessage(messages, currentEditedMesssageIndex, 'down', currentUsername);
        } else {
            nextMessageIndex = -1;
        }
    }

    return messages.find(message => isMessageEditable(message, currentUsername) && messages[nextMessageIndex]?.id === message?.id);
};

export const getInitialRoomMembers = memoize(messages => {
    const roomMemberKey = [];
    const roomMembers = [];

    for (let i = messages.length - 1; i >= 0; i--) {
        const { username, name } = get(messages[i], 'user');

        if (username !== 'affectli_rocket_admin' && !roomMemberKey.includes(username) && roomMemberKey.length <= 5) {
            roomMemberKey.push(username);
            roomMembers.push({
                username,
                name,
                isSuggested: true,
            });
        }

        if (roomMemberKey.length === 5) {
            break;
        }
    }

    return roomMembers;
});

export const getCurrentWord = inputRef => {
    if (!inputRef?.current) return '';

    const input = inputRef.current;
    const start = input.selectionStart;
    const value = input.value;

    let text = '';

    for (let i = start - 1; i >= 0; i--) {
        if (value[i] === ' ' || value[i] === '' || value[i] === '\n') break;
        text = value[i] + text;
    }

    return text;
};

export const findPreviousAtSymbol = (text, position) => {
    let index = position;
    while (index >= 0 && (text[index] !== '@' || (text[index] === '@' && text[index - 1] !== ' ' && text[index - 1] !== '' && text[index - 1] !== '\n'))) {
        index--;
    }
    return index >= 0 ? index : 0;
};

export const filterMentionedUsers = (inputRef, users, mentionedUsers) => {
    const predefinedUsers = [
        { _id: 'all', username: 'all' },
        { _id: 'all', username: 'here' },
    ];
    const searchInputValue = getCurrentWord(inputRef).substring(1);
    const query = !isEmpty(searchInputValue) ? searchInputValue.trim().toLowerCase() : '';

    const filterUsername = users => {
        return (users || []).filter(user => {
            const { username, name } = user || {};
            let isFound = false;
            if (isEmpty(user)) return isFound;

            if (username) {
                const parsedUsername = username.toLowerCase();
                isFound = parsedUsername.indexOf(query) >= 0;
                if (isFound) return true;
            }

            if (name) {
                const parsedName = name.toLowerCase();
                isFound = parsedName.indexOf(query) >= 0;
                if (isFound) return true;
            }

            return isFound;
        });
    };

    const withMentionedUsers = [...filterUsername(users), ...mentionedUsers, ...filterUsername(predefinedUsers)];

    const removedDuplicateUsers = withMentionedUsers.filter((user, index, arr) => index === arr.findIndex(u => u.username === user.username)).slice(-6);

    return removedDuplicateUsers;
};

export const getUserStatus = (isSuggested, outside) => {
    if (isSuggested) {
        return 'Suggestion from recent messages';
    }
    if (outside) {
        return 'Not in channel';
    }
    return '';
};

export const setInputTextMention = (inputRef, username) => {
    const { selectionStart: currentPosition, value: inputValue } = inputRef.current;
    const startPosition = findPreviousAtSymbol(inputValue, currentPosition);
    const newText = inputValue.substring(0, startPosition) + `@${username} ` + inputValue.substring(currentPosition);
    return { newText, cursorPosition: startPosition + username.length + 2 };
};

// Match `:shortcode:` tokens. RC shortcodes use `[a-z0-9_+-]`; case-insensitive for safety.
const SHORTCODE_RE = /:([a-z0-9_+-]+):/gi;

const resolveShortcodeGlyph = match => {
    const viaRc = rcEmojiDataCache ? resolveShortcodeToNative(match, rcEmojiDataCache) : null;
    if (viaRc) return viaRc;
    const viaJoy = joypixels.shortnameToUnicode(match);
    return viaJoy && viaJoy !== match ? viaJoy : null;
};

// Replace `:shortcode:` tokens with native unicode emojis. Tries the bundled RC dataset
// first (covers `_toneN` + `flag_xx` shortcodes joypixels can't handle), then falls back
// to joypixels. Unknown shortcodes are left untouched so custom emojis and false positives
// pass through. Used for plain-text insertion (compose input).
export const convertShortcodesToUnicode = text => {
    if (!text) return text;
    return text.replace(SHORTCODE_RE, match => resolveShortcodeGlyph(match) || match);
};

// Wraps emoji glyphs (both `:shortcode:` tokens AND bare unicode emoji chars) in
// `<span class="chat-emoji-glyph">UNICODE</span>` so rendered messages can style them
// larger than surrounding text.
// Why normalize unicode → shortcode first: compose input inserts unicode directly so
// the SHORTCODE_RE alone misses those. `joypixels.toShort` rewrites known unicode
// emojis back to `:shortname:` form, then the single pass below wraps both kinds.
// RC sizing conventions: emoji-only message → 44×44; emoji inline with text → 24×24.
// When the message is emoji-only (no non-whitespace text outside shortcodes) we tag
// each glyph with `chat-emoji-glyph--big` so CSS can scale to 44px.
export const renderShortcodesAsEmojiHtml = text => {
    if (!text) return text;
    const normalized = joypixels.toShort(text);
    const withoutShortcodes = normalized.replace(SHORTCODE_RE, '');
    const isEmojiOnly = withoutShortcodes.trim().length === 0;
    const cls = isEmojiOnly ? 'chat-emoji-glyph chat-emoji-glyph--big' : 'chat-emoji-glyph';
    return normalized.replace(SHORTCODE_RE, match => {
        const glyph = resolveShortcodeGlyph(match);
        return glyph ? `<span class="${cls}">${glyph}</span>` : match;
    });
};

export const parseMessage = (text, mentions, primary, callback) => {
    let parsedTextValue = renderShortcodesAsEmojiHtml(text);
    if (!isEmpty(mentions)) {
        mentions.forEach(({ username }) => {
            let linkClassName = '';
            const isMentionTypeUser = !['all', 'here'].includes(username);
            if (isMentionTypeUser) {
                const isCurrentUser = primary.username === username;
                linkClassName = isCurrentUser ? 'mention-link-me' : 'mention-link-user';
            } else {
                linkClassName = 'mention-link-group';
            }
            parsedTextValue = replaceAll(parsedTextValue, `@${username}`, `<a class='${linkClassName}'>${username}</a>`);
            callback(parsedTextValue);
        });
    } else {
        callback(parsedTextValue);
    }
};

export const focusChatInput = inputRef => {
    if (inputRef.current) {
        inputRef.current.focus();
    }
};

export const setChatInputCursorToLast = inputRef => {
    inputRef.current.scrollTop = inputRef.current.scrollHeight;
    const value = inputRef.current.value || '';
    const inputLength = value.length;
    inputRef.current.setSelectionRange(inputLength, inputLength);
};

export const getCredentials = state => {
    const token = get(state, 'user.chat.token');
    const userId = get(state, 'user.chat.id');
    if (!token || !userId) {
        throw new Error('the chat credentials are not available in the profile.');
    }
    return { userId, token };
};

export const isChatExist = (state, msgId, roomId) => {
    const chatRoomMessages = get(state, `chat.room.messages`, {});
    const chatRoomData = get(state, `chat.room.data`, []);

    if (!isEmpty(chatRoomMessages[roomId])) {
        const selectedRoomMsg = chatRoomMessages[roomId].find(({ id }) => id === msgId);
        return !isEmpty(selectedRoomMsg) ? selectedRoomMsg.text : false;
    } else {
        const selectedChatRoom = chatRoomData.find(({ _id }) => _id === roomId);
        const { lastMessage: { _id: lastMsgId, text } = {} } = selectedChatRoom || {};
        return lastMsgId === msgId ? text : false;
    }
};

const normalizeIncomingReactions = raw =>
    Object.entries(raw || {}).map(([emoji, data]) => ({
        emoji,
        usernames: (data && data.usernames) || [],
        names: (data && data.names) || [],
        count: ((data && data.usernames) || []).length,
    }));

// Resolve an RC shortcode (with or without colons, with or without `_toneN` suffix) to
// its native Unicode glyph using the bundled RC emoji dataset. joypixels can't handle
// RC-specific tone-suffixed shortcodes, so we look up the hex in rcSupportedEmojis.json
// and convert codepoints ourselves.
export const resolveShortcodeToNative = (shortcode, rcData) => {
    if (!shortcode || !rcData || !rcData.emojis) return null;
    const stripped = String(shortcode).replace(/^:|:$/g, '');
    const toneMatch = stripped.match(/^(.+)_tone([1-5])$/);
    const baseId = toneMatch ? toneMatch[1] : stripped;
    const toneIdx = toneMatch ? parseInt(toneMatch[2], 10) : 0;
    const rec = rcData.emojis[baseId];
    if (!rec || !rec.s) return null;
    const skin = rec.s.find(s => (s.t || 0) === toneIdx) || rec.s[0];
    if (!skin || !skin.u) return null;
    return skin.u
        .split('-')
        .map(code => String.fromCodePoint(parseInt(code, 16)))
        .join('');
};

// Diff two reaction arrays. Returns list of { emoji, username, name, action: 'add'|'remove' }
// for each (emoji, user) pair that differs between prev and next.
// Uses \u0000 as map-key separator — emoji shortcodes are colon-wrapped (e.g. `:heart:`) so
// a `:` delimiter would collide and leave stray colons on the split parts.
const REACTION_KEY_SEP = '\u0000';
export const diffReactions = (prevReactions, nextReactions) => {
    const prevMap = new Map();
    (prevReactions || []).forEach(r => {
        (r.usernames || []).forEach((u, i) => {
            prevMap.set(`${r.emoji}${REACTION_KEY_SEP}${u}`, (r.names && r.names[i]) || u);
        });
    });
    const nextMap = new Map();
    (nextReactions || []).forEach(r => {
        (r.usernames || []).forEach((u, i) => {
            nextMap.set(`${r.emoji}${REACTION_KEY_SEP}${u}`, (r.names && r.names[i]) || u);
        });
    });
    const changes = [];
    nextMap.forEach((name, key) => {
        if (!prevMap.has(key)) {
            const [emoji, username] = key.split(REACTION_KEY_SEP);
            changes.push({ emoji, username, name, action: 'add' });
        }
    });
    prevMap.forEach((name, key) => {
        if (!nextMap.has(key)) {
            const [emoji, username] = key.split(REACTION_KEY_SEP);
            changes.push({ emoji, username, name, action: 'remove' });
        }
    });
    return changes;
};

const serializeReactions = arr =>
    (arr || [])
        .map(r => `${r.emoji}:${(r.usernames || []).slice().sort().join(',')}`)
        .sort()
        .join('|');

// Reaction updates arrive on stream-room-messages with unchanged msg text.
// Returns true when the incoming reactions differ from what's already in state.
export const hasReactionsChanged = (state, msgId, roomId, incomingReactionsRaw) => {
    const chatRoomMessages = get(state, `chat.room.messages`, {});
    const list = chatRoomMessages[roomId];
    if (isEmpty(list)) return false;
    const existing = list.find(({ id }) => id === msgId);
    if (isEmpty(existing)) return false;
    const a = serializeReactions(existing.reactions);
    const b = serializeReactions(normalizeIncomingReactions(incomingReactionsRaw));
    return a !== b;
};

// Same not-in-state→false contract as hasReactionsChanged.
export const hasThreadMetaChanged = (state, msgId, roomId, incoming) => {
    const list = get(state, `chat.room.messages.${roomId}`) || [];
    const existing = list.find(({ id }) => id === msgId);
    if (isEmpty(existing)) return false;
    const inTlm = get(incoming, 'tlm.$date', get(incoming, 'tlm', null));
    const exTlm = existing.tlm ? new Date(existing.tlm).getTime() : null;
    return (
        (existing.tcount || 0) !== (incoming.tcount || 0) ||
        JSON.stringify([...(existing.replies || [])].sort()) !== JSON.stringify([...(incoming.replies || [])].sort()) ||
        exTlm !== (inTlm ? new Date(inTlm).getTime() : null)
    );
};

// Subscription type → A-Live route (mirrors the messageReceived link logic).
// For type 'p': entity chats route by rel.type; plain groups/teams (customFields present
// with no entity rel.type, or teamId set) route to team/group.
export const buildAliveLink = (subType, rel, customFields) => {
    switch (subType) {
        case 'p': {
            if (get(customFields, 'teamId')) return `/abox/alive/team/${rel.id}`;
            if (rel.type) return `/abox/alive/${rel.type}/${rel.id}`;
            return `/abox/alive/group/${rel.id}`;
        }
        case 'c': return `/abox/alive/channel/${rel.id}`;
        case 'l': return `/abox/alive/live/${rel.id}`;
        case 'd':
        default: return `/abox/alive/direct/${rel.id}`;
    }
};

// Thread timestamps: within 24h show time of day ("11:36 PM"), older show the
// full date ("June 15, 2026").
export const formatThreadTime = date => {
    if (!date) return '';
    const d = moment(date);
    return moment().diff(d, 'hours') < 24 ? d.format('h:mm A') : d.format('MMMM DD, YYYY');
};

// Full date + time for thread tooltips, e.g. "June 15, 2026 12:36 PM".
export const formatThreadDateTime = date => (date ? moment(date).format('MMMM DD, YYYY h:mm A') : '');

// Chat message header timestamp (12hr), e.g. "11:19 PM, 16 Jun '26" — matches the message headers.
export const formatChatTimestamp = date => (date ? moment(date).format(`h:mm A, DD MMM 'YY`) : '');

export const normalizeMessage = message => {
    const { editedBy } = message || {};
    const ts = new Date(get(message, 'ts', ''));
    const tsDate = new Date(get(message, 'ts.$date', ''));
    const editedAt = new Date(get(message, 'editedAt.$date', ''));
    const createDate = isDateValid(tsDate) ? tsDate : ts;
    const editedDate = isDateValid(editedAt) ? editedAt : null;

    return {
        id: message._id,
        type: message.t,
        text: message.msg,
        file: message.file && {
            id: message.file._id,
            name: message.file.name,
            type: message.file.type,
            description: get(message, 'attachments[0].description'),
        },
        md: message.md || [],
        mentions: message.mentions || [],
        reactions: Object.entries(message.reactions || {}).map(([emoji, data]) => ({
            emoji,
            usernames: data.usernames || [],
            names: data.names || [],
            count: (data.usernames || []).length,
        })),
        // Thread fields (Rocket.Chat): parent carries tcount/replies/tlm, replies carry tmid.
        tmid: message.tmid,
        tcount: message.tcount,
        replies: message.replies || [],
        tlm: (() => {
            const tlmDate = new Date(get(message, 'tlm.$date', get(message, 'tlm', '')));
            return isDateValid(tlmDate) ? tlmDate : null;
        })(),
        createDate,
        editedDate,
        editedBy,
        unread: message.unread,
        user: {
            id: get(message, 'u._id'),
            username: get(message, 'u.username'),
            name: get(message, 'u.name'),
        },
    };
};

// Thread messages: same as normalizeMessage, but RC system messages (e.g. 't:rm' = removed)
// get their A-Live display text ("Message removed") while keeping thread meta + file. So a
// removed reply renders the "Message removed" tombstone (like A-Live) instead of a blank body.
export const normalizeThreadMessage = raw => {
    const normalized = normalizeMessage(raw);
    // getSystemMessageByType reads `raw.msg` (`.match`) — guard against a system message
    // without a msg string so it can't throw and break the whole thread load.
    if (raw && raw.t && typeof raw.msg === 'string') {
        return { ...normalized, text: getSystemMessageByType(raw).text };
    }
    return normalized;
};

export const getUserMeta = (state, username) => {
    const userList = get(state, `admin.users.references.data`, []);
    if (!Boolean(userList.length) && !username) return false;
    const { id, image } = userList.find(u => u.username === username) || {};
    return !id ? false : { id, image };
};
